import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import {
  CheckCircle,
  WarningCircle,
  XCircle,
  Lightning,
  Plugs,
  ArrowRight,
  CircleNotch,
  CaretDown,
} from '@phosphor-icons/react';

const STATUS_CONFIG = {
  ready: {
    label: 'Ready',
    color: '#00E5C0',
    bg: 'rgba(0,229,192,0.06)',
    border: 'rgba(0,229,192,0.15)',
    Icon: CheckCircle,
    description: 'All capabilities connected and ready to execute',
  },
  partially_ready: {
    label: 'Partially Ready',
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.06)',
    border: 'rgba(245,158,11,0.15)',
    Icon: WarningCircle,
    description: 'Some capabilities need tool connections',
  },
  not_ready: {
    label: 'Not Ready',
    color: '#EF4444',
    bg: 'rgba(239,68,68,0.06)',
    border: 'rgba(239,68,68,0.15)',
    Icon: XCircle,
    description: 'Connect required tools to activate this worker',
  },
};

export function ReadinessWidget({ workerId, onConnect, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewExpanded, setPreviewExpanded] = useState(false);

  const fetchReadiness = useCallback(async () => {
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/workers/${workerId}/readiness`);
      const d = await res.json();
      setData(d);
    } catch {
      // silently fail — widget is non-critical
    } finally {
      setLoading(false);
    }
  }, [workerId]);

  useEffect(() => { fetchReadiness(); }, [fetchReadiness, refreshKey]);

  if (loading) {
    return (
      <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-5 mb-5 flex items-center justify-center h-20" data-testid="readiness-loading">
        <CircleNotch size={18} className="text-[#5C6778] animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const status = STATUS_CONFIG[data.overall_status] || STATUS_CONFIG.not_ready;
  const totalCaps = data.capabilities?.total || 0;
  const readyCaps = data.capabilities?.ready || 0;
  const hasBlocked = (data.blocked || []).length > 0;
  const hasPreview = (data.execution_preview || []).length > 0;

  return (
    <div
      className="rounded-xl border bg-[#1E2836] p-5 mb-5"
      style={{ borderColor: status.border }}
      data-testid="readiness-widget"
    >
      {/* Status header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <status.Icon size={20} weight="fill" style={{ color: status.color }} />
          <div>
            <span className="text-sm font-semibold text-[#F0F4F8]" data-testid="readiness-status-label">
              {status.label}
            </span>
            {totalCaps > 0 && (
              <span className="text-[11px] text-[#7D8896] ml-2">
                {readyCaps}/{totalCaps} capabilities ready
              </span>
            )}
          </div>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ color: status.color, background: status.bg }} data-testid="readiness-badge">
          {data.overall_status === 'ready' ? 'All systems go' : data.overall_status === 'partially_ready' ? 'Action needed' : 'Setup required'}
        </span>
      </div>

      {/* Capability checklist */}
      {(totalCaps > 0) && (
        <div className="space-y-1 mb-4" data-testid="capability-checklist">
          {/* Ready capabilities */}
          {(data.ready || []).map((cap, i) => (
            <div key={`r-${i}`} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-[rgba(0,229,192,0.03)]" data-testid={`cap-ready-${i}`}>
              <CheckCircle size={14} weight="fill" className="text-[#00E5C0] shrink-0" />
              <span className="text-[12px] text-[#F0F4F8] flex-1">{cap.label}</span>
              <span className="text-[10px] text-[#5C6778]">{cap.tool}</span>
            </div>
          ))}

          {/* Blocked capabilities */}
          {(data.blocked || []).map((cap, i) => (
            <div key={`b-${i}`} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-[rgba(239,68,68,0.03)] border border-[rgba(239,68,68,0.06)]" data-testid={`cap-blocked-${i}`}>
              <XCircle size={14} weight="fill" className="text-[#EF4444] shrink-0" />
              <span className="text-[12px] text-[#9CA3B0] flex-1">{cap.label}</span>
              <div className="flex items-center gap-1">
                {(cap.connect_options || []).slice(0, 2).map((opt, j) => (
                  <button
                    key={j}
                    onClick={() => onConnect && onConnect(opt.slug)}
                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.14)] transition-colors"
                    data-testid={`connect-btn-${opt.slug}`}
                  >
                    <Plugs size={10} />
                    Connect {opt.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Execution preview */}
      {hasPreview && (
        <div data-testid="execution-preview">
          <button
            onClick={() => setPreviewExpanded(!previewExpanded)}
            className="flex items-center gap-2 w-full text-left py-2"
            data-testid="execution-preview-toggle"
          >
            <CaretDown
              size={10}
              className={`text-[#5C6778] transition-transform ${previewExpanded ? '' : '-rotate-90'}`}
            />
            <Lightning size={12} className="text-[#7D8896]" />
            <span className="text-[11px] text-[#7D8896] font-medium">What happens when this runs</span>
          </button>

          {previewExpanded && (
            <div className="pl-5 space-y-0.5 mt-1" data-testid="execution-preview-steps">
              {(data.execution_preview || []).map((step, i) => {
                const isBlocked = step.status === 'blocked';
                return (
                  <div key={i} className="flex items-start gap-2 py-1.5">
                    {/* Step connector line */}
                    <div className="flex flex-col items-center shrink-0 pt-0.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${isBlocked ? 'bg-[#EF4444]' : 'bg-[#00E5C0]'}`} />
                      {i < (data.execution_preview.length - 1) && (
                        <div className="w-px h-4 bg-[rgba(255,255,255,0.06)] mt-0.5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-[11px] ${isBlocked ? 'text-[#7D8896]' : 'text-[#9CA3B0]'}`}>
                        {step.description}
                      </span>
                      {step.tool && !isBlocked && (
                        <span className="text-[9px] text-[#5C6778] ml-1.5">via {step.tool}</span>
                      )}
                      {isBlocked && step.blocked_reason && (
                        <span className="text-[9px] text-[#EF4444] ml-1.5">{step.blocked_reason}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
